'use client';

import React, { useEffect, useRef, useState } from 'react';

import { useRouter, useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';

import { faArrowsRotate, faCaretDown, faCaretUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Code, Divider, Flex, List, ListItem, Loader, PasswordInput, Text, TextInput } from '@mantine/core';
import { isNotEmpty, useForm } from '@mantine/form';

import { NliSmallBlueButton } from '@repo/ui/components/nli-button';
import { NliDarkOutlinedButton } from '@repo/ui/components/nli-button';
import { NliContainer } from '@repo/ui/components/nli-container';
import { NliTitle } from '@repo/ui/components/nli-title';

import { NliApiError, useAppDisplayRequestErrors } from '@hooks/useAppDisplayRequestErrors';

import { useAppContext } from '@context/AppContext';
import { useHateoasContext } from '@context/HateoasContext';

import { LOGIN_PROVIDER_STORAGE_KEY, PENDING_LOGIN_PROVIDER_STORAGE_KEY } from '@utils/login-provider-preference';
import { getPublicApiUrl } from '@utils/runtime-config';

import { loginAction, loginBoondAction } from '../actions';

const getSafeInternalPath = (value: string | null): string =>
    value && value.startsWith('/') && !value.startsWith('//') ? value : '/';

export default function Page(): React.JSX.Element {
    const t = useTranslations();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [legacyLoginVisible, setLegacyLoginVisible] = useState(false);
    const [oauthEnabled, setOauthEnabled] = useState(false);
    const [errorCodeVisible, setErrorCodeVisible] = useState(false);

    const displayRequestErrors = useAppDisplayRequestErrors();
    const [iframe] = useAppContext('iframe');
    const [, setLogout] = useAppContext('logout');
    const { clearAll } = useHateoasContext();
    const router = useRouter();
    const searchParams = useSearchParams();
    const hasResetContextRef = useRef(false);

    const errorCode = searchParams.get('errorCode');
    const oauthError = searchParams.get('oauthError');
    const from = getSafeInternalPath(searchParams.get('from'));
    const signedRequest = searchParams.get('signedRequest');

    const formLogin = useForm({
        mode: 'uncontrolled',
        validate: {
            emailLogin: isNotEmpty(t('required')),
            passwordLogin: isNotEmpty(t('required')),
        },
    });

    useEffect(() => {
        if (hasResetContextRef.current) {
            return;
        }

        hasResetContextRef.current = true;
        setLogout();
        clearAll();
        sessionStorage.removeItem('main-provider.bootstrap-cache.v1');
    }, [clearAll, setLogout]);

    useEffect(() => {
        const apiUrl = getPublicApiUrl();
        localStorage.setItem(LOGIN_PROVIDER_STORAGE_KEY, 'boond');

        if (iframe) {
            setLoading(false);
            return;
        }

        if (!apiUrl) {
            setOauthEnabled(false);
            setLoading(false);
            return;
        }

        fetch(`${apiUrl.replace(/\/$/, '')}/auth/boond/oauth/config`, { cache: 'no-store' })
            .then((response) => response.ok ? response.json() : null)
            .then((config) => {
                setOauthEnabled(Boolean(config?.enabled));
            })
            .catch(() => {
                setOauthEnabled(false);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [iframe]);

    const onLoginSubmit = async (values: { emailLogin: string; passwordLogin: string }) => {
        setSubmitting(true);
        const { emailLogin, passwordLogin } = values;
        try {
            const result = await loginAction({ email: emailLogin, password: passwordLogin }, 'boond');

            // Vérifier si la connexion a réussi
            if (result.success) {
                localStorage.setItem(LOGIN_PROVIDER_STORAGE_KEY, 'boond');
                sessionStorage.removeItem(PENDING_LOGIN_PROVIDER_STORAGE_KEY);
                router.push(from);
            } else {
                throw result.error;
            }
        } catch (err: unknown) {
            // Gestion des erreurs non attendues
            displayRequestErrors(err as NliApiError, undefined, true, true);
        } finally {
            setSubmitting(false);
        }
    };

    const loginBoond = async () => {
        if (signedRequest) {
            const result: { success: true } | { success: false, error?: { message: string } } = await loginBoondAction(signedRequest);
            if (result.success) {
                localStorage.setItem(LOGIN_PROVIDER_STORAGE_KEY, 'boond');
                sessionStorage.removeItem(PENDING_LOGIN_PROVIDER_STORAGE_KEY);
                router.push('/');
            } else {
                if (result.error?.message === 'COMPANY_NOT_FOUND') {
                    router.push('/unregistred');
                }
                let [, errorCode] = typeof result.error?.message === 'string' ? result.error.message.split(':') : [];
                if (!errorCode) {
                    errorCode = '-1';
                }
                router.push(`/login?signedRequest=${signedRequest}&errorCode=${errorCode}`);
            }
        }
    };

    const startOAuthLogin = () => {
        sessionStorage.setItem(PENDING_LOGIN_PROVIDER_STORAGE_KEY, 'boond');
        const params = new URLSearchParams();
        if (from && from !== '/') {
            params.set('from', from);
        }
        window.location.href = `/auth/boond/oauth/start${params.toString() ? `?${params.toString()}` : ''}`;
    };

    return (
        <NliContainer
            mt="lg"
            style={{
                minHeight: '230px',
            }}
        >
            <Flex direction="column" gap="md" h="100%" justify="center" align="center">
                {loading ?
                    <Flex justify="center" py="xl">
                        <Loader color="nli-liberty" />
                    </Flex>
                    : iframe ? (
                        <>
                            <NliTitle mb="sm" order={1}>
                                {t('temporarily-restricted-access-to-neylia')}
                            </NliTitle>
                            <Flex direction="column">
                                <Text size="sm">
                                    {t('you-are-indeed-in-a-neylia-environment-but')}
                                </Text>
                                <List size="sm">
                                    <ListItem>
                                        {t('either-your-session-has-expired-and-you-are-no-longer-logged-in')}
                                    </ListItem>
                                    <ListItem>
                                        {t('or-your-individual-or-company-account-is-not-active')}
                                    </ListItem>
                                </List>
                            </Flex>
                            <Flex direction="column">
                                <Text size="sm">
                                    {t('what-should-i-do')}
                                </Text>
                                <List size="sm">
                                    <ListItem>
                                        {t('try-refreshing-the-page-with-f5')}
                                    </ListItem>
                                    <ListItem>
                                        {t('try-uninstalling-reinstalling-the-application')}
                                    </ListItem>
                                    <ListItem>
                                        {t('if-the-problem-persists-contact-the-person-in-charge-of-neylia-in-your-company-the-admin-to-verify-your-access-rights')}
                                    </ListItem>
                                </List>
                            </Flex>
                            {
                                signedRequest ? (
                                    <Flex direction="column" justify="center" align="center">
                                        <NliSmallBlueButton onClick={loginBoond} leftSection={<FontAwesomeIcon icon={faArrowsRotate} />}>
                                            {t('refresh')}
                                        </NliSmallBlueButton>
                                    </Flex>
                                ) : null
                            }
                            {
                                errorCode && errorCode !== '-1' && (
                                    <Flex direction="column" gap={4}>
                                        <Divider
                                            label={errorCodeVisible ? <FontAwesomeIcon icon={faCaretUp} /> : <FontAwesomeIcon icon={faCaretDown} />}
                                            labelPosition="right"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => setErrorCodeVisible((v) => !v)}
                                        />
                                        {errorCodeVisible && <Code ta="center">
                                            {t('error-code')}
                                            {' '}
                                    :
                                            {' '}
                                            {errorCode}
                                        </Code>}
                                    </Flex>
                                )
                            }
                        </>
                    ) : (
                            <Flex direction="column" gap="md" w="320px">
                                <NliTitle order={2} ta="center">
                                    Boond
                                </NliTitle>
                                {!oauthEnabled && (
                                    <Flex direction="column">
                                        <Text size="sm" ta="center">
                                            {t('enter-your-boondmanager-credentials-to-log-in')}
                                        </Text>
                                    </Flex>
                                )}
                                {oauthError && (
                                    <Code ta="center">
                        OAuth2 BoondManager :
                                        {' '}
                                        {oauthError}
                                    </Code>
                                )}
                                {oauthEnabled && (
                                    <Flex direction="column" align="center" gap="sm">
                                        <NliDarkOutlinedButton aria-label="Se connecter avec Boond" onClick={startOAuthLogin}>
                            Se connecter avec Boond
                                        </NliDarkOutlinedButton>
                                        <Divider label="Ou" w="320px" />
                                        {!legacyLoginVisible && (
                                            <NliSmallBlueButton onClick={() => setLegacyLoginVisible(true)}>
                                    Autre moyen de connexion
                                            </NliSmallBlueButton>
                                        )}
                                    </Flex>
                                )}
                                {!oauthEnabled || legacyLoginVisible ? (
                                    <Flex justify="center">
                                        <form onSubmit={formLogin.onSubmit((values) => onLoginSubmit(values))} style={{ width: '320px' }}>
                                            <Flex direction="column" style={{ textAlign: 'center' }}>
                                                <TextInput
                                                    {...formLogin.getInputProps('emailLogin')}
                                                    key={formLogin.key('emailLogin')}
                                                    label={t('login')}
                                                    withAsterisk
                                                />
                                                <PasswordInput
                                                    {...formLogin.getInputProps('passwordLogin')}
                                                    key={formLogin.key('passwordLogin')}
                                                    mt="md"
                                                    label={t('password')}
                                                    withAsterisk
                                                />
                                            </Flex>
                                            <Flex mt="lg" justify="center" py="xs">
                                                <NliDarkOutlinedButton aria-label={t('connection')} type="submit" loading={submitting}>
                                                    {t('log-in')}
                                                </NliDarkOutlinedButton>
                                            </Flex>
                                        </form>
                                    </Flex>
                                ) : null}
                            </Flex>
                    )}
            </Flex>
        </NliContainer>
    );
}
